import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Translate, Check } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

// Featured languages requested by the user (in the order they should appear at
// the top of the menu). The rest of the list is alphabetical below a divider.
const FEATURED = [
  { code: "es", label: "Español" },
  { code: "ru", label: "Русский" },
  { code: "en", label: "English" },
  { code: "zh", label: "中文 (普通话)" },
];

const OTHERS = [
  { code: "ar", label: "العربية" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "hi", label: "हिन्दी" },
  { code: "it", label: "Italiano" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "pt", label: "Português" },
  { code: "tr", label: "Türkçe" },
];

/**
 * Language picker with FOUR featured languages on top (Español, Русский,
 * English, 中文) and the rest of the supported languages alphabetically below.
 * Only es + en have full translations today; other selections persist on the
 * backend (so they can be activated later) and fall back to en strings.
 */
export default function LanguageMenu({ collapsed = false }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const all = [...FEATURED, ...OTHERS];
  const current = all.find((l) => l.code === lang) || FEATURED[0];

  const pick = (code) => {
    setLang(code, { persist: true });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="lang-toggle"
          aria-label="Cambiar idioma"
          className="flex items-center gap-2 px-3 py-2 w-full text-xs text-gray-400 hover:text-[#CCFF00] transition-colors mb-2"
        >
          <Translate size={16} />
          {!collapsed && <span className="truncate">{current.label}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="w-56 p-1.5 bg-[#0F0F0F] border-white/15 max-h-[60vh] overflow-y-auto"
        data-testid="lang-menu"
      >
        <p className="px-2 py-1 text-[9px] tracking-[0.2em] uppercase font-bold text-[#CCFF00]">Preferidos</p>
        {FEATURED.map((l) => (
          <button
            key={l.code}
            onClick={() => pick(l.code)}
            data-testid={`lang-option-${l.code}`}
            className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm text-left rounded hover:bg-white/5 ${lang === l.code ? "text-[#CCFF00] font-semibold" : "text-gray-200"}`}
          >
            <span>{l.label}</span>
            {lang === l.code && <Check size={14} weight="bold" />}
          </button>
        ))}
        <div className="my-1 h-px bg-white/10" />
        <p className="px-2 py-1 text-[9px] tracking-[0.2em] uppercase font-bold text-gray-500">Más idiomas</p>
        {OTHERS.map((l) => (
          <button
            key={l.code}
            onClick={() => pick(l.code)}
            data-testid={`lang-option-${l.code}`}
            className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm text-left rounded hover:bg-white/5 ${lang === l.code ? "text-[#CCFF00] font-semibold" : "text-gray-300"}`}
          >
            <span>{l.label}</span>
            {lang === l.code && <Check size={14} weight="bold" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
